import { Box, Card, Grid, MenuItem, Typography, Alert, Stack, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomButton from "../../components/customButton";
import { CircleUser, ClipboardCheck, User, Calendar, Download } from "lucide-react";

import CustomSelect from "../../components/customSelect";
import ProfileCard from "../../components/employeeProfileCards/profileCard";
import StaticDateRangePicker from "../../components/staticDateRangePicker";
import DailyLogCard from "../../components/card/logsCard";
import PaginatedTable from "../../components/dynamicTable";
import useWorkerStore from "../../zustand/useWorkerStore";
import { openSnackAlert } from "../../helper";
import { getAttendanceData } from "../../services/Workers";
import { format } from "date-fns";
import AppLoader from "../../components/appLoader";

/** Multiple breaks come as `breaks[]`; still support legacy `breakIn` / `breakOut`. */
function getBreakTimesDisplay(record, type) {
  if (!record) return "-";
  if (Array.isArray(record.breaks) && record.breaks.length > 0) {
    const times = record.breaks
      .filter((b) => b?.type === type && b?.time)
      .map((b) => b.time);
    return times.length > 0 ? times.join(", ") : "-";
  }
  if (type === "breakIn" && record.breakIn?.time) return record.breakIn.time;
  if (type === "breakOut" && record.breakOut?.time) return record.breakOut.time;
  return "-";
}

function escapeCsvCell(val) {
  const str = val == null ? "" : String(val);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function normalizeBreaksList(record) {
  if (!record) return [];
  if (Array.isArray(record.breaks) && record.breaks.length > 0) {
    return record.breaks.filter((b) => b?.time);
  }
  const legacy = [];
  if (record.breakIn?.time) {
    legacy.push({
      type: "breakIn",
      time: record.breakIn.time,
      status: record.breakIn.status,
    });
  }
  if (record.breakOut?.time) {
    legacy.push({
      type: "breakOut",
      time: record.breakOut.time,
      status: record.breakOut.status,
    });
  }
  return legacy;
}

function formatAttendanceCommentsForCsv(comments) {
  if (!Array.isArray(comments) || comments.length === 0) return "-";
  const lines = comments
    .map((c) => String(c?.comment ?? "").trim())
    .filter(Boolean);
  return lines.length > 0 ? lines.join("; ") : "-";
}

function breakTypeShortLabel(type) {
  if (type === "breakOut") return "Out";
  if (type === "breakIn") return "In";
  return type || "—";
}

function AttendanceBreaksCell({ record }) {
  const list = normalizeBreaksList(record);
  if (!list.length) {
    return (
      <Typography component="span" fontSize={12} color="text.secondary">
        —
      </Typography>
    );
  }
  const sessions = [];
  for (let i = 0; i < list.length; i += 2) {
    sessions.push(list.slice(i, i + 2));
  }
  return (
    <Stack spacing={0.75} alignItems="stretch" sx={{ width: "100%" }}>
      {sessions.map((pair, idx) => (
        <Box
          key={idx}
          sx={{
            borderRadius: "10px",
            border: "1px solid #C8E6C9",
            bgcolor: "#F1F8F4",
            px: 1,
            py: 0.2,
            textAlign: "left",
          }}
        >
          <Typography
            component="div"
            fontSize={10}
            fontWeight={700}
            color="#225B27"
            letterSpacing={0.3}
            sx={{ mb: 0.35 }}
          >
            Break {idx + 1}
          </Typography>
          <Stack spacing={0.15}>
            {pair.map((b, j) => (
              <Stack
                key={j}
                direction="row"
                spacing={1}
                alignItems="baseline"
                justifyContent="space-between"
                sx={{ gap: 0.5 }}
              >
                <Typography
                  component="span"
                  fontSize={8}
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ minWidth: 28 }}
                >
                  {breakTypeShortLabel(b.type)}
                </Typography>
                <Typography
                  component="span"
                  fontSize={8}  
                  fontWeight={600}
                  color="text.primary"
                  sx={{ flex: 1, textAlign: "right" }}
                >
                  {b.time}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function AttendanceCommentsCell({ comments }) {
  const list = Array.isArray(comments)
    ? comments.filter((c) => String(c?.comment ?? "").trim())
    : [];
  if (!list.length) {
    return (
      <Typography component="span" fontSize={12} color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} alignItems="center" textAlign="left" justifyContent="center" sx={{ width: "100%" }}>
      {list.map((c, idx) => (
        <Tooltip title={c.comment} arrow placement="top">
          <Typography
            key={c._id || idx}
            fontSize={12}
            color="text.primary"
            fontWeight={500}
            textAlign="center"

          >
            {String(c.comment).trim().slice(0, 20)}...
          </Typography>
        </Tooltip>
      ))}
    </Stack>
  );
}

const AttendanceTracking = () => {
  const { allWorkers, apiLoading, initializeData } = useWorkerStore();
  const [attendanceData, setAttendanceData] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState("");
  const [dateRange, setDateRange] = useState([
    new Date(), // 30 days ago
    new Date(), // Today
  ]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  // Reset attendance data when component mounts
  useEffect(() => {
    setAttendanceData(null);
    setSelectedWorker("");
  }, []);

  const attendanceTableHeaders = [
    { id: "date", title: "Date", align: "center" },
    { id: "checkIn", title: "Check In", align: "center" },
    { id: "breaksDisplay", title: "Breaks", align: "left" },
    { id: "checkOut", title: "Check Out", align: "center" },
    { id: "workingHours", title: "Working Hours", align: "center" },
    { id: "status", title: "Status", align: "center" },
    { id: "commentsDisplay", title: "Comments", align: "center" },
  ];

  const handleGetAttendanceData = async (workerId, startDate, endDate) => {
    if (!workerId) return;

    setLoading(true);
    try {
      // Format dates to YYYY-MM-DD format
      const formattedStartDate = format(startDate, "yyyy-MM-dd");
      const formattedEndDate = format(endDate, "yyyy-MM-dd");

      console.log("Sending dates:", { formattedStartDate, formattedEndDate });

      const response = await getAttendanceData(
        workerId,
        formattedStartDate,
        formattedEndDate
      );
      const attendanceResponse = response?.data;
      if (response.status === 200 || response.status === 201) {
        setAttendanceData(attendanceResponse.data);
        console.log(attendanceResponse.data, "attendanceData");
      } else {
        openSnackAlert(
          attendanceResponse?.message || "Failed to fetch attendance data",
          "error"
        );
        setAttendanceData(null);
      }
    } catch (error) {
      openSnackAlert(error?.message || "Something went wrong", "error");
      setAttendanceData(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle worker selection change
  const handleWorkerChange = (event) => {
    const workerId = event.target.value;
    setSelectedWorker(workerId);
    if (workerId && dateRange[0] && dateRange[1]) {
      handleGetAttendanceData(workerId, dateRange[0], dateRange[1]);
    }
  };

  // Handle date range change
  const handleDateRangeChange = (newValue) => {
    setDateRange(newValue);
    if (selectedWorker && newValue[0] && newValue[1]) {
      handleGetAttendanceData(selectedWorker, newValue[0], newValue[1]);
    }
  };

  // Convert attendance history to table data
  const getTableData = () => {
    if (!attendanceData?.attendanceHistory) return [];

    return attendanceData.attendanceHistory.map((record) => ({
      date: format(new Date(record.date), "dd/MM/yyyy"),
      checkIn: record.checkIn?.time || "-",
      checkOut: record.checkOut?.time || "-",
      breaksDisplay: <AttendanceBreaksCell record={record} />,
      workingHours: record.workingHours || 0,
      status: record.status || "-",
      commentsDisplay: (
        <AttendanceCommentsCell comments={record.attendanceComments} />
      ),
    }));
  };

  // Get selected worker details
  const getSelectedWorkerDetails = () => {
    if (!selectedWorker || !allWorkers.length) return null;
    return allWorkers.find((worker) => worker._id === selectedWorker);
  };

  // CSV Export function
  const exportToCSV = () => {
    if (!attendanceData?.attendanceHistory || attendanceData.attendanceHistory.length === 0) {
      openSnackAlert("No data available to export", "warning");
      return;
    }

    const selectedWorkerDetails = getSelectedWorkerDetails();
    const workerName = selectedWorkerDetails 
      ? `${selectedWorkerDetails.firstName} ${selectedWorkerDetails.lastName}`
      : "Unknown Worker";

    // Prepare CSV headers
    const headers = [
      "Date",
      "Check In",
      "Check Out",
      "Break In",
      "Break Out",
      "Working Hours",
      "Status",
      "Comments",
    ];
    
    // Prepare CSV data
    const csvData = attendanceData.attendanceHistory.map((record) => [
      format(new Date(record.date), "dd/MM/yyyy"),
      record.checkIn?.time || "-",
      record.checkOut?.time || "-",
      getBreakTimesDisplay(record, "breakIn"),
      getBreakTimesDisplay(record, "breakOut"),
      record.workingHours || 0,
      record.status || "-",
      formatAttendanceCommentsForCsv(record.attendanceComments),
    ]);

    // Create CSV content
    const csvContent = [
      `Employee: ${workerName}`,
      `Date Range: ${format(dateRange[0], "dd/MM/yyyy")} - ${format(dateRange[1], "dd/MM/yyyy")}`,
      `Generated: ${format(new Date(), "dd/MM/yyyy HH:mm")}`,
      "",
      headers.join(","),
      ...csvData.map((row) => row.map(escapeCsvCell).join(",")),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `attendance_${workerName.replace(/\s+/g, "_")}_${format(dateRange[0], "yyyy-MM-dd")}_to_${format(dateRange[1], "yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    openSnackAlert("Attendance data exported successfully!", "success");
  };

  const displayRows = attendanceTableHeaders.map((header) => header.id);
  const selectedWorkerDetails = getSelectedWorkerDetails();

  return (
    <>
      <Box
        bgcolor="primary.bgLight"
        padding={"20px"}
        borderRadius={"22px"}
        mt={0}
      >
        <AppLoader isVisible={loading} />
        <Grid container spacing={2}>
          <Grid item sm={12} md={5}>
            <Typography variant="h4">Attendance Tracking</Typography>
            <Typography variant="description">
              Track employee attendance and status
            </Typography>
          </Grid>
          <Grid item sm={12} spacing={2} md={7}>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "flex-end",
              }}
            >
              <CustomSelect
                name="worker"
                placeholder="Select Worker"
                value={selectedWorker}
                onChange={handleWorkerChange}
                endIcon={<User size={16} />}
                sx={{ width: "100%", height: "36px" }}
                InputWidth="40%"
              >
                {allWorkers.map((worker) => (
                  <MenuItem key={worker._id} value={worker._id}>
                    {worker.firstName} {worker.lastName}
                  </MenuItem>
                ))}
              </CustomSelect>
            </Box>
          </Grid>
        </Grid>

        {/* Empty State - Show when no worker is selected */}
        {!selectedWorker ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
              textAlign: "center",
            }}
          >
            <Calendar size={64} color="#9E9E9E" />
            <Typography variant="h5" sx={{ mt: 2, color: "text.secondary" }}>
              Please Select Worker
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, color: "text.secondary" }}>
              Choose a worker from the dropdown above to view their attendance
              data
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {/* Profile Card */}
              <Grid item xs={12} sm={12} md={12} lg={3}>
                {selectedWorkerDetails && (
                  <ProfileCard
                    employee={{
                      name: `${selectedWorkerDetails.firstName} ${selectedWorkerDetails.lastName}`,
                      designation:
                        selectedWorkerDetails.designation || "Employee",
                      id:
                        selectedWorkerDetails.employeeId ||
                        selectedWorkerDetails._id,
                      phone:
                        selectedWorkerDetails.contact ||
                        selectedWorkerDetails.phone,
                      email: selectedWorkerDetails.email,
                      photo:
                        selectedWorkerDetails.profileImage ||
                        "https://cdn.vectorstock.com/i/500p/22/63/a-simple-black-silhouette-of-man-in-suit-vector-55942263.jpg",
                      checkIn: attendanceData?.todayData?.checkIn?.time || "-",
                      checkOut:
                        attendanceData?.todayData?.checkOut?.time || "-",
                    }}
                  />
                )}
              </Grid>
              {/* Analytics Cards */}
              <Grid item xs={12} sm={12} md={12} lg={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    {attendanceData?.todayData && (
                      <DailyLogCard
                        title="Today's Log"
                        inName="Check In"
                        outName="Check Out"
                        breaks={normalizeBreaksList(attendanceData.todayData)}
                        inTime={attendanceData.todayData.checkIn?.time || "-"}
                        outTime={attendanceData.todayData.checkOut?.time || "-"}
                        totalLate={`${
                          attendanceData.summary?.lateCount || 0
                        } Late`}
                        status={attendanceData.todayData.status || "Incomplete"}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={6}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <StaticDateRangePicker
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        sx={{
                          border: "2px solid #2E7D32",
                          borderRadius: "18px",
                          bgcolor: "#fff",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Box mt={3}></Box>
              </Grid>
            </Grid>

            {/* Attendance Table */}
            <Box mt={1}>
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "200px",
                  }}
                >
                  <Typography>Loading attendance data...</Typography>
                </Box>
              ) : attendanceData?.attendanceHistory?.length > 0 ? (
                <>
                  {/* Export Button */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mb: 2,
                    }}
                  >
                    <CustomButton
                      variant="contained"
                      btnLabel="Export CSV" 
                      startIcon={<Download size={16} />}
                      handlePressBtn={exportToCSV}
                      sx={{
                        backgroundColor: "#2E7D32",
                        "&:hover": {
                          backgroundColor: "#1B5E20",
                        },
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 3,
                        py: 1,
                      }}
                    />
                  </Box>
                  
                  <PaginatedTable
                    tableHeader={attendanceTableHeaders}
                    tableData={getTableData()}
                    displayRows={displayRows}
                    showPagination={false}
                  />
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "200px",
                  }}
                >
                  <Typography color="text.secondary">
                    No attendance data found for the selected date range
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default AttendanceTracking;
